import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Avatar,
  AvatarGroup,
  Tooltip,
  Checkbox,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Badge,
  Menu,
  ListItemIcon,
  ListItemText,
  Backdrop,
  Fade,
  Zoom,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarToday,
  Assignment,
  People,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Search,
  Download,
  Visibility,
  MoreVert,
  Close,
  EventNote,
  Person,
  Business,
  TrendingUp,
  TrendingDown,
  Warning,
  Info,
  DoneAll,
  Email,
  Phone,
  BarChart,
  Save,
  ShowChart,
  Timeline,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney,
  School,
  Work,
  Favorite,
  Star,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, parseISO, isWithinInterval, startOfDay, isAfter, isBefore, differenceInDays, eachMonthOfInterval, subMonths } from 'date-fns';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  ComposedChart
} from 'recharts';

import Sidebar from '../components/sidebars/Sidebar';
import TopNav from '../components/top_bars/TopNav';
import StatisticsCards from '../components/top_bars/StatisticsCard';

// Styled Components
const StatusBadge = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'approved' ? theme.palette.success.main :
                  status === 'pending' ? theme.palette.warning.main :
                  status === 'rejected' ? theme.palette.error.main :
                  status === 'cancelled' ? theme.palette.grey[500] :
                  theme.palette.info.main,
  color: '#fff',
  fontWeight: 'bold',
  '& .MuiChip-label': {
    padding: '0 8px',
    fontSize: '0.75rem'
  }
}));

const LeaveTypeChip = styled(Chip)(({ theme, type }) => {
  const colors = {
    annual: '#2196f3',
    sick: '#f44336',
    maternity: '#e91e63',
    paternity: '#9c27b0',
    unpaid: '#ff9800',
    study: '#4caf50',
    compassionate: '#795548'
  };
  return {
    backgroundColor: colors[type] || '#999',
    color: '#fff',
    fontWeight: 'bold',
    '& .MuiChip-label': {
      fontSize: '0.75rem'
    }
  };
});

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8]
  }
}));

const GradientCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)'
  }
}));

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const HrLeaveDashboard = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    daysUsed: 0,
    daysRemaining: 30
  });
  const [filters, setFilters] = useState({
    status: 'all',
    leave_type: 'all',
    department: 'all',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departments, setDepartments] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [bulkActionMenu, setBulkActionMenu] = useState(null);
  const [currentUser, setCurrentUser] = useState({
    phone: '',
    name: '',
    department: '',
    email: '',
    user_type: ''
  });
  const [trendPeriod, setTrendPeriod] = useState('6months');
  const [selectedMetric, setSelectedMetric] = useState('requests');
  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // Leave types configuration
  const leaveTypes = [
    { value: 'annual', label: 'Annual Leave', color: '#2196f3', icon: <CalendarToday /> },
    { value: 'sick', label: 'Sick Leave', color: '#f44336', icon: <Warning /> },
    { value: 'maternity', label: 'Maternity Leave', color: '#e91e63', icon: <Favorite /> },
    { value: 'paternity', label: 'Paternity Leave', color: '#9c27b0', icon: <Star /> },
    { value: 'unpaid', label: 'Unpaid Leave', color: '#ff9800', icon: <AttachMoney /> },
    { value: 'study', label: 'Study Leave', color: '#4caf50', icon: <School /> },
    { value: 'compassionate', label: 'Compassionate Leave', color: '#795548', icon: <Work /> }
  ];

  // Load current user
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setInitialLoading(true);
    try {
      const userPhone = localStorage.getItem('userPhone') || localStorage.getItem('phone');
      const userName = localStorage.getItem('userName') || localStorage.getItem('full_name') || 'HR Manager';
      const userDept = localStorage.getItem('userDepartment') || localStorage.getItem('department') || 'HR';
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');
      
      if (userPhone) {
        setCurrentUser({
          phone: userPhone,
          name: userName,
          department: userDept,
          email: userEmail || '',
          user_type: 'admin'
        });
      }
      
      fetchDepartments();
      fetchLeaveRequests();
      fetchLeaveStats();
    } catch (error) {
      console.error('Error loading user data:', error);
      showSnackbar('Error loading user data', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_depts_list.php`);
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  // Fetch leave requests
  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.leave_type !== 'all') params.append('leave_type', filters.leave_type);
      if (filters.department !== 'all') params.append('department', filters.department);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/leave_requests.php?${params}`
      );
      
      if (response.data.success) {
        setLeaveRequests(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching leave requests:', err);
      showSnackbar('Error fetching leave requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch leave statistics
  const fetchLeaveStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/leave_stats.php`);
      
      if (response.data.success) {
        setLeaveStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // CRITICAL FIX: Watch for filter changes and refetch data
  useEffect(() => {
    if (!initialLoading) {
      fetchLeaveRequests();
    }
  }, [filters.status, filters.leave_type, filters.department, filters.search, initialLoading]);

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/leave_requests.php?id=${requestId}`,
        {
          action: 'approve',
          approved_by: currentUser.name || 'HR Manager'
        }
      );
      
      if (response.data.success) {
        showSnackbar('Leave request approved successfully', 'success');
        fetchLeaveRequests();
        fetchLeaveStats();
        setDialogOpen(false);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Error approving request', 'error');
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/leave_requests.php?id=${requestId}`,
        {
          action: 'reject',
          approved_by: currentUser.name || 'HR Manager',
          rejection_reason: reason
        }
      );
      
      if (response.data.success) {
        showSnackbar('Leave request rejected', 'warning');
        fetchLeaveRequests();
        fetchLeaveStats();
        setDialogOpen(false);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Error rejecting request', 'error');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedRequests.length === 0) {
      showSnackbar('No requests selected', 'warning');
      return;
    }
    
    for (const requestId of selectedRequests) {
      if (action === 'approve') {
        await handleApproveRequest(requestId);
      } else if (action === 'reject') {
        const reason = prompt('Please provide reason for rejection:');
        if (reason) await handleRejectRequest(requestId, reason);
      }
    }
    
    setBulkActionMenu(null);
    setSelectedRequests([]);
  };

  // Calculate statistics from leave requests
  const calculateStats = () => {
    const pending = leaveRequests.filter(r => r.status === 'pending').length;
    const approved = leaveRequests.filter(r => r.status === 'approved').length;
    const rejected = leaveRequests.filter(r => r.status === 'rejected').length;
    const totalDaysApproved = leaveRequests
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + (r.duration_days || 0), 0);
    
    return { pending, approved, rejected, totalDaysApproved, total: leaveRequests.length };
  };

  const stats = calculateStats();

  // Generate department trend data
  const getDepartmentTrends = () => {
    const deptStats = {};
    departments.forEach(dept => {
      const deptRequests = leaveRequests.filter(r => r.department === dept.departments);
      deptStats[dept.departments] = {
        total: deptRequests.length,
        approved: deptRequests.filter(r => r.status === 'approved').length,
        pending: deptRequests.filter(r => r.status === 'pending').length,
        rejected: deptRequests.filter(r => r.status === 'rejected').length,
        totalDays: deptRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.duration_days || 0), 0),
        approvalRate: deptRequests.length > 0 ? 
          ((deptRequests.filter(r => r.status === 'approved').length / deptRequests.length) * 100).toFixed(1) : 0
      };
    });
    
    return Object.entries(deptStats).map(([name, data]) => ({
      name,
      ...data
    })).sort((a, b) => b.total - a.total);
  };

  // Generate monthly trend data
  const getMonthlyTrends = () => {
    const months = [];
    const now = new Date();
    let monthsCount = trendPeriod === '3months' ? 3 : trendPeriod === '6months' ? 6 : 12;
    
    for (let i = monthsCount - 1; i >= 0; i--) {
      const date = subMonths(now, i);
      const monthName = format(date, 'MMM yyyy');
      const monthRequests = leaveRequests.filter(r => {
        const requestDate = parseISO(r.start_date);
        return requestDate.getMonth() === date.getMonth() && 
               requestDate.getFullYear() === date.getFullYear();
      });
      
      months.push({
        month: monthName,
        requests: monthRequests.length,
        days: monthRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.duration_days || 0), 0),
        approved: monthRequests.filter(r => r.status === 'approved').length,
        pending: monthRequests.filter(r => r.status === 'pending').length,
        rejected: monthRequests.filter(r => r.status === 'rejected').length
      });
    }
    
    return months;
  };

  // Generate leave type distribution
  const getLeaveTypeDistribution = () => {
    const distribution = {};
    leaveTypes.forEach(type => {
      const count = leaveRequests.filter(r => r.leave_type === type.value).length;
      const days = leaveRequests
        .filter(r => r.leave_type === type.value && r.status === 'approved')
        .reduce((sum, r) => sum + (r.duration_days || 0), 0);
      distribution[type.label] = { count, days, color: type.color };
    });
    
    return Object.entries(distribution).map(([name, data]) => ({
      name,
      value: selectedMetric === 'requests' ? data.count : data.days,
      days: data.days,
      count: data.count,
      color: data.color
    }));
  };

  const departmentTrends = getDepartmentTrends();
  const monthlyTrends = getMonthlyTrends();
  const leaveTypeDistribution = getLeaveTypeDistribution();

  const renderStatCards = () => {
    const statItems = [
      {
        title: 'Pending Requests',
        value: stats.pending,
        icon: <Pending sx={{ fontSize: 40 }} />,
        trend: stats.pending > 0 ? `${stats.pending} awaiting` : 'All clear',
        trendUp: stats.pending > 0,
        bgGradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
      },
      {
        title: 'Approved Requests',
        value: stats.approved,
        icon: <CheckCircle sx={{ fontSize: 40 }} />,
        trend: `${stats.approved} approved`,
        trendUp: true,
        bgGradient: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)'
      },
      {
        title: 'Rejected Requests',
        value: stats.rejected,
        icon: <Cancel sx={{ fontSize: 40 }} />,
        trend: `${stats.rejected} rejected`,
        trendUp: false,
        bgGradient: 'linear-gradient(135deg, #f44336 0%, #ff7961 100%)'
      },
      {
        title: 'Total Requests',
        value: stats.total,
        icon: <Assignment sx={{ fontSize: 40 }} />,
        trend: `${stats.total} total requests`,
        trendUp: true,
        bgGradient: 'linear-gradient(135deg, #009688 0%, #4db6ac 100%)'
      }
    ];
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statItems.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <StyledCard sx={{ background: stat.bgGradient, color: '#fff' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h3" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                      {stat.title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                      {stat.trendUp ? 
                        <TrendingUp sx={{ fontSize: 14 }} /> : 
                        <TrendingDown sx={{ fontSize: 14 }} />
                      }
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {stat.trend}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ opacity: 0.8 }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderAnalyticsCharts = () => {
    return (
      <Box>
        {/* Trend Period Selector */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h6" fontWeight="bold">
            Analytics & Trends
          </Typography>
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={trendPeriod}
                onChange={(e) => setTrendPeriod(e.target.value)}
                sx={{ bgcolor: '#fff' }}
              >
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="12months">Last 12 Months</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                sx={{ bgcolor: '#fff' }}
              >
                <MenuItem value="requests">Request Count</MenuItem>
                <MenuItem value="days">Total Days</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Monthly Trends Chart */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Monthly {selectedMetric === 'requests' ? 'Request' : 'Days'} Trends
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={monthlyTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip />
                  <Legend />
                  {selectedMetric === 'requests' ? (
                    <>
                      <Bar yAxisId="left" dataKey="requests" fill="#8884d8" name="Requests" />
                      <Line yAxisId="right" type="monotone" dataKey="approved" stroke="#82ca9d" name="Approved" />
                    </>
                  ) : (
                    <>
                      <Bar yAxisId="left" dataKey="days" fill="#ffc658" name="Total Days" />
                      <Line yAxisId="right" type="monotone" dataKey="approved" stroke="#8884d8" name="Approved Requests" />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Leave Type Distribution Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Leave Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leaveTypeDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {leaveTypeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box mt={2}>
                {leaveTypeDistribution.map((type, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: COLORS[index % COLORS.length] }} />
                      <Typography variant="body2">{type.name}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {type.value} {selectedMetric === 'requests' ? 'requests' : 'days'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Department Performance Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Department Performance Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <RechartsBarChart
                  data={departmentTrends.slice(0, 8)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="total" fill="#8884d8" name="Total Requests" />
                  <Bar yAxisId="left" dataKey="approved" fill="#82ca9d" name="Approved" />
                  <Line yAxisId="right" type="monotone" dataKey="approvalRate" stroke="#ff7300" name="Approval Rate %" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Department Stats Cards */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Department Breakdown
            </Typography>
            <Grid container spacing={2}>
              {departmentTrends.slice(0, 6).map((dept, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <StyledCard>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {dept.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {dept.total} total requests
                          </Typography>
                        </Box>
                        <Chip
                          label={`${dept.approvalRate}% Approval`}
                          size="small"
                          color={dept.approvalRate >= 70 ? 'success' : dept.approvalRate >= 50 ? 'warning' : 'error'}
                        />
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Approved</Typography>
                          <Typography variant="h6" color="success.main">{dept.approved}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Pending</Typography>
                          <Typography variant="h6" color="warning.main">{dept.pending}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Rejected</Typography>
                          <Typography variant="h6" color="error.main">{dept.rejected}</Typography>
                        </Grid>
                      </Grid>
                      <LinearProgress
                        variant="determinate"
                        value={dept.approvalRate}
                        sx={{ mt: 2, height: 6, borderRadius: 3 }}
                        color={dept.approvalRate >= 70 ? 'success' : dept.approvalRate >= 50 ? 'warning' : 'error'}
                      />
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Key Insights */}
          <Grid item xs={12}>
            <GradientCard>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Key Insights
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Most Active Department
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {departmentTrends[0]?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption">
                          {departmentTrends[0]?.total || 0} total requests
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Star sx={{ fontSize: 40, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Highest Approval Rate
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {departmentTrends.sort((a, b) => b.approvalRate - a.approvalRate)[0]?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption">
                          {departmentTrends.sort((a, b) => b.approvalRate - a.approvalRate)[0]?.approvalRate || 0}% approval rate
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CalendarToday sx={{ fontSize: 40, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Most Popular Leave Type
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {leaveTypeDistribution.sort((a, b) => b.value - a.value)[0]?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption">
                          {leaveTypeDistribution.sort((a, b) => b.value - a.value)[0]?.value || 0} {selectedMetric === 'requests' ? 'requests' : 'days'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </GradientCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderLeaveRequestsTable = () => {
    const paginatedRequests = leaveRequests.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            Leave Requests
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search employee..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.leave_type}
                onChange={(e) => setFilters({ ...filters, leave_type: e.target.value })}
                displayEmpty
              >
                <MenuItem value="all">All Types</MenuItem>
                {leaveTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                displayEmpty
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.departments}>{dept.departments}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchLeaveRequests();
                fetchLeaveStats();
              }}
            >
              Refresh
            </Button>
            {selectedRequests.length > 0 && (
              <Button
                variant="contained"
                startIcon={<DoneAll />}
                onClick={(e) => setBulkActionMenu(e.currentTarget)}
              >
                Bulk Action ({selectedRequests.length})
              </Button>
            )}
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRequests.length === paginatedRequests.length && paginatedRequests.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRequests(paginatedRequests.map(r => r.id));
                      } else {
                        setSelectedRequests([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Dates</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary">No leave requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRequests.includes(request.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRequests([...selectedRequests, request.id]);
                          } else {
                            setSelectedRequests(selectedRequests.filter(id => id !== request.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {request.employee_name?.charAt(0) || 'E'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {request.employee_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{request.department}</TableCell>
                    <TableCell>
                      <LeaveTypeChip
                        label={leaveTypes.find(t => t.value === request.leave_type)?.label || request.leave_type}
                        type={request.leave_type}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{request.duration_days} days</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(parseISO(request.start_date), 'MMM dd')} - {format(parseISO(request.end_date), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={request.status.toUpperCase()}
                        status={request.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRequest(request);
                          setDialogOpen(true);
                        }}
                        title="View Details"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      {request.status === 'pending' && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveRequest(request.id)}
                            title="Approve"
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const reason = prompt('Please provide reason for rejection:');
                              if (reason) handleRejectRequest(request.id, reason);
                            }}
                            title="Reject"
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {leaveRequests.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(leaveRequests.length / rowsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    );
  };

  const renderViewDialog = () => {
    if (!selectedRequest) return null;
    
    return (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">Leave Request Details</Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Employee Information
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 32 }}>
                    {selectedRequest.employee_name?.charAt(0) || 'E'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {selectedRequest.employee_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedRequest.department}
                    </Typography>
                    <Box display="flex" gap={2} mt={0.5}>
                      <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                        <Email fontSize="inherit" /> {selectedRequest.email}
                      </Typography>
                      <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                        <Phone fontSize="inherit" /> {selectedRequest.phone}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Leave Details
                </Typography>
                <Box mt={1}>
                  <Typography variant="body1" mb={1}>
                    <strong>Type:</strong>{' '}
                    <LeaveTypeChip 
                      label={leaveTypes.find(t => t.value === selectedRequest.leave_type)?.label || selectedRequest.leave_type} 
                      type={selectedRequest.leave_type} 
                      size="small" 
                    />
                  </Typography>
                  <Typography variant="body1" mb={1}>
                    <strong>Duration:</strong> {selectedRequest.duration_days} days
                  </Typography>
                  <Typography variant="body1" mb={1}>
                    <strong>Start Date:</strong> {format(parseISO(selectedRequest.start_date), 'EEEE, MMMM dd, yyyy')}
                  </Typography>
                  <Typography variant="body1" mb={1}>
                    <strong>End Date:</strong> {format(parseISO(selectedRequest.end_date), 'EEEE, MMMM dd, yyyy')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Status Information
                </Typography>
                <Box mt={1}>
                  <Typography variant="body1" mb={1}>
                    <strong>Status:</strong>{' '}
                    <StatusBadge label={selectedRequest.status.toUpperCase()} status={selectedRequest.status} size="small" />
                  </Typography>
                  {selectedRequest.approved_by && (
                    <Typography variant="body1" mb={1}>
                      <strong>Approved/Rejected By:</strong> {selectedRequest.approved_by}
                    </Typography>
                  )}
                  {selectedRequest.approved_date && (
                    <Typography variant="body1" mb={1}>
                      <strong>Action Date:</strong> {format(parseISO(selectedRequest.approved_date), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                  {selectedRequest.rejection_reason && (
                    <Typography variant="body1" mb={1}>
                      <strong>Rejection Reason:</strong> {selectedRequest.rejection_reason}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Reason for Leave
                </Typography>
                <Typography variant="body1" mt={1}>
                  {selectedRequest.reason || 'No reason provided'}
                </Typography>
                
                {selectedRequest.handover_notes && (
                  <>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom mt={2}>
                      Handover Notes
                    </Typography>
                    <Typography variant="body1" mt={1}>
                      {selectedRequest.handover_notes}
                    </Typography>
                  </>
                )}
                
                {selectedRequest.contact_during_leave && (
                  <>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom mt={2}>
                      Contact During Leave
                    </Typography>
                    <Typography variant="body1" mt={1}>
                      {selectedRequest.contact_during_leave}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {selectedRequest.status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApproveRequest(selectedRequest.id)}
                startIcon={<CheckCircle />}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  const reason = prompt('Please provide reason for rejection:');
                  if (reason) handleRejectRequest(selectedRequest.id, reason);
                }}
                startIcon={<Cancel />}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (initialLoading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 9999 }}>
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8f9fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1600px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.clear();
            window.location.href = '/login';
          }}
          onNotificationClick={() => showSnackbar('You have new notifications!', 'info')}
          toggleSidebar={toggleSidebar}
        />
        
        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>
        
        <Fade in timeout={500}>
          <Box sx={{ mt: 4 }}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<DashboardIcon />} label="Overview" />
              <Tab icon={<Assignment />} label="Leave Requests" />
              <Tab icon={<BarChart />} label="Analytics & Trends" />
            </Tabs>
            
            {/* Overview Tab */}
            {activeTab === 0 && (
              <Box>
                {renderStatCards()}
                
                {/* Quick Stats Summary */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Recent Activity
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell>Leave Type</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {leaveRequests.slice(0, 5).map((request) => (
                              <TableRow key={request.id} hover>
                                <TableCell>{request.employee_name}</TableCell>
                                <TableCell>
                                  <LeaveTypeChip
                                    label={leaveTypes.find(t => t.value === request.leave_type)?.label || request.leave_type}
                                    type={request.leave_type}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <StatusBadge label={request.status.toUpperCase()} status={request.status} size="small" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Top Departments by Requests
                      </Typography>
                      {departmentTrends.slice(0, 5).map((dept, index) => (
                        <Box key={index} mb={2}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">{dept.name}</Typography>
                            <Typography variant="body2" fontWeight="bold">{dept.total} requests</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(dept.total / (departmentTrends[0]?.total || 1)) * 100}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Analytics & Trends Tab */}
            {activeTab === 2 && renderAnalyticsCharts()}
            
            {/* Leave Requests Tab */}
            {activeTab === 1 && renderLeaveRequestsTable()}
          </Box>
        </Fade>
      </Box>
      
      {/* Dialogs */}
      {renderViewDialog()}
      
      {/* Bulk Action Menu */}
      <Menu
        anchorEl={bulkActionMenu}
        open={Boolean(bulkActionMenu)}
        onClose={() => setBulkActionMenu(null)}
      >
        <MenuItem onClick={() => handleBulkAction('approve')}>
          <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>Approve Selected</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('reject')}>
          <ListItemIcon><Cancel fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Reject Selected</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HrLeaveDashboard;